import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';

const STORAGE_KEY = 'rr.activeBot';
// Not under ['bots']: invalidating the list after adding a chatbot used to
// refetch this too, read the old choice from storage before the new one was
// written, and put it back over the new one (22 September 2026).
const ACTIVE_KEY = ['active-bot'] as const;

function remembered(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

/**
 * The organisation's chatbots, and the one the console is looking at
 * (21 September 2026).
 *
 * An organisation with one chatbot never sees a chooser: `multi` is false,
 * `active` is that one bot, and every page behaves exactly as it did when
 * there was only "the bot". With two or more, the Flows and Knowledge pages
 * show one bot's work at a time, and the choice is shared between them (and
 * remembered on this device), so picking Thabo on Flows is still Thabo on
 * Knowledge.
 *
 * The choice lives in the query cache rather than in a component, which is
 * what lets a sidebar and a page that never meet agree on it without a
 * provider threaded through the shell.
 */
export function useBots() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['bots'], queryFn: () => flows.listBots(), staleTime: 30_000 });
  const chosen = useQuery({ queryKey: ACTIVE_KEY, queryFn: () => remembered(), staleTime: Infinity, gcTime: Infinity });

  const bots = useMemo(() => list.data?.data ?? [], [list.data]);
  const defaultBot = useMemo(() => bots.find((b) => b.is_default) ?? bots[0] ?? null, [bots]);
  const active = useMemo(
    () => bots.find((b) => b.id === chosen.data) ?? defaultBot,
    [bots, chosen.data, defaultBot]
  );

  const setActive = useCallback((id: string) => {
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* storage may be unavailable */ }
    qc.setQueryData(ACTIVE_KEY, id);
  }, [qc]);

  /** Is this flow the active bot's? A flow that names no bot is the default bot's. */
  const owns = useCallback(
    (flow: { bot_id?: string | null }) => !active || flow.bot_id === active.id || (!flow.bot_id && active.is_default),
    [active]
  );

  const allowance = list.data?.meta?.allowance;
  /** May another chatbot be added on this plan? Unknown counts as yes: the service has the last word. */
  const canAdd = !allowance || allowance.unlimited || allowance.allowance == null || bots.length < allowance.allowance;

  return { bots, multi: bots.length > 1, active, defaultBot, setActive, owns, allowance, canAdd, isPending: list.isPending, error: list.error };
}

/** The name a bot is shown by, never blank. */
export function botName(bot: { name?: string | null } | null | undefined, fallback: string): string {
  const name = (bot?.name ?? '').trim();
  return name.length > 0 ? name : fallback;
}
